import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/gapApi';
import { Building2, Search, AlertTriangle, CheckCircle2, Star, TrendingDown, ArrowRight, Zap, Lightbulb, Target, History as HistoryIcon, X } from 'lucide-react';
import RecentAnalysisPanel from '../components/RecentAnalysisPanel';

const HISTORY_ENDPOINT = '/gap-analysis/history';

export const ReviewGapAnalysis = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const historyId = searchParams.get('historyId');

  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReplay, setLoadingReplay] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Replay mode: a Recent Analyses (or Dashboard/History tab) click lands here
  // with ?historyId=<id> — fetch the saved result instead of running a live analysis.
  useEffect(() => {
    if (!historyId) return;
    let cancelled = false;
    setLoadingReplay(true);
    setError('');
    api.get(`${HISTORY_ENDPOINT}/${historyId}`)
      .then(({ data }) => { if (!cancelled) setResult(data); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load saved analysis.');
      })
      .finally(() => { if (!cancelled) setLoadingReplay(false); });
    return () => { cancelled = true; };
  }, [historyId]);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!companyName) {
      setError('Company Name is required');
      return;
    }

    setSearchParams({}, { replace: true });
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await api.post('/gap-analysis/analyze', {
        company_name: companyName,
        website: website
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred during analysis');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return {
          badge: 'text-red-400 bg-red-400/10 border-red-400/30 shadow-[0_0_10px_rgba(248,113,113,0.2)]',
          border: 'border-l-red-500',
          icon: <AlertTriangle className="text-red-400" size={18} />
        };
      case 'medium':
        return {
          badge: 'text-orange-400 bg-orange-400/10 border-orange-400/30 shadow-[0_0_10px_rgba(251,146,60,0.2)]',
          border: 'border-l-orange-500',
          icon: <Zap className="text-orange-400" size={18} />
        };
      case 'low':
        return {
          badge: 'text-blue-400 bg-blue-400/10 border-blue-400/30 shadow-[0_0_10px_rgba(96,165,250,0.2)]',
          border: 'border-l-blue-500',
          icon: <Lightbulb className="text-blue-400" size={18} />
        };
      default:
        return {
          badge: 'text-slate-400 bg-slate-400/10 border-slate-400/30',
          border: 'border-l-slate-500',
          icon: <Target className="text-slate-400" size={18} />
        };
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-12 relative overflow-hidden">
      {/* Decorative Background Elements */}
      {/* <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div> */}
      {/* <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/20 rounded-full blur-[120px] pointer-events-none"></div> */}

      <div className="max-w-7xl mx-auto relative z-10">

        {/* Header Section */}
        <div className="mb-10 text-center animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="inline-flex items-center justify-center p-3 bg-blue-500/10 rounded-2xl mb-4 border border-blue-500/20">
            <TrendingDown className="text-blue-400 w-8 h-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-slate-400 mb-4 tracking-tight">
            Review & Gap Analysis
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Uncover the hidden delta between your brand's messaging and the actual customer experience through AI-driven intelligence.
          </p>
        </div>

        {/* Input Form Glassmorphism Card */}
        <div className="bg-slate-900/60 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.3)] mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
          <form onSubmit={handleAnalyze} className="flex flex-col md:flex-row gap-6 items-end">
            <div className="flex-1 w-full group">
              <label className="block text-sm font-semibold text-slate-300 mb-2 tracking-wide">Company Name *</label>
              <div className="relative overflow-hidden rounded-xl">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-400 transition-colors z-10" size={20} />
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 focus:bg-slate-800 transition-all duration-300"
                  required
                />
              </div>
            </div>

            <div className="flex-1 w-full group">
              <label className="block text-sm font-semibold text-slate-300 mb-2 tracking-wide">Website (Optional)</label>
              <div className="relative overflow-hidden rounded-xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-400 transition-colors z-10" size={20} />
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="e.g. acme.com"
                  className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 focus:bg-slate-800 transition-all duration-300"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto h-[60px] px-10 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 disabled:hover:from-blue-600 disabled:hover:to-indigo-600 text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transform hover:-translate-y-1 active:translate-y-0"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <span>Analyze Gaps</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {error && (
            <div className="mt-6 p-4 bg-red-500/10 backdrop-blur-sm border border-red-500/30 text-red-400 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
              <AlertTriangle size={20} className="shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}
        </div>

        {historyId && (
          <div className="mb-12 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between gap-3 animate-in fade-in">
            <span className="text-sm text-blue-300 flex items-center gap-2">
              <HistoryIcon size={16} /> {loadingReplay ? 'Loading saved analysis...' : 'Viewing a saved analysis from history.'}
            </span>
            <button
              type="button"
              onClick={() => { setSearchParams({}, { replace: true }); setResult(null); }}
              className="text-xs font-bold text-blue-300 hover:text-white flex items-center gap-1 transition-colors"
            >
              <X size={14} /> Close
            </button>
          </div>
        )}

        <RecentAnalysisPanel
          historyEndpoint={HISTORY_ENDPOINT}
          activeId={historyId}
          onSelect={(id) => setSearchParams({ historyId: id })}
          onDeleted={(id) => { if (id === historyId) { setSearchParams({}, { replace: true }); setResult(null); } }}
        />

        {/* Results Section */}
        {!loading && result && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">

            {/* Top Grid: Profile & Reviews */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">

              {/* Company Profile Card */}
              <div className="lg:col-span-1 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-7 border border-slate-700/50 shadow-xl flex flex-col max-h-[550px] transform hover:border-slate-600 transition-colors duration-300">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700/50 pb-4 shrink-0">
                  <div className="p-2 bg-slate-800 rounded-lg border border-slate-700">
                    <Building2 className="text-blue-400" size={20} />
                  </div>
                  <h2 className="text-xl font-bold text-white">Company Profile</h2>
                </div>

                <div className="space-y-5 overflow-y-auto pr-3 custom-scrollbar flex-1">
                  <div className="group">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 group-hover:text-blue-400 transition-colors">Name</div>
                    <div className="text-white font-semibold text-lg">{result.company_profile.name}</div>
                  </div>

                  <div className="group">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 group-hover:text-blue-400 transition-colors">Industry</div>
                    <div className="inline-block px-3 py-1 bg-slate-800 border border-slate-700 rounded-full text-slate-300 text-sm">
                      {result.company_profile.industry || 'Unknown'}
                    </div>
                  </div>

                  <div className="group">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 group-hover:text-blue-400 transition-colors">Company Size</div>
                    <div className="text-slate-300 font-medium">
                      {result.company_profile.employee_count === 'None-None'
                        ? 'Not specified'
                        : `${result.company_profile.employee_count} employees`}
                    </div>
                  </div>

                  <div className="group pb-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 group-hover:text-blue-400 transition-colors">Description</div>
                    <div className="text-slate-300 text-sm leading-loose whitespace-pre-line bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                      {result.company_profile.description || 'No description available'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sample Reviews Card */}
              <div className="lg:col-span-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl p-7 border border-slate-700/50 shadow-xl flex flex-col max-h-[550px] transform hover:border-slate-600 transition-colors duration-300">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-700/50 pb-4 shrink-0">
                  <div className="p-2 bg-slate-800 rounded-lg border border-slate-700">
                    <Star className="text-yellow-400" size={20} />
                  </div>
                  <h2 className="text-xl font-bold text-white flex-1">Customer Reviews Analyzed</h2>
                  <span className="text-xs font-bold bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full border border-blue-500/20">
                    {result.reviews.length} total
                  </span>
                </div>

                <div className="space-y-4 overflow-y-auto pr-3 custom-scrollbar flex-1">
                  {result.reviews.length > 0 ? (
                    result.reviews.map((review, idx) => (
                      <div key={idx} className="bg-slate-800/50 hover:bg-slate-800 p-5 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-all duration-300 hover:shadow-lg group">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-bold tracking-widest uppercase px-3 py-1 bg-slate-900 text-slate-300 rounded-full border border-slate-700 shadow-sm group-hover:border-slate-500 transition-colors">
                            {review.source}
                          </span>
                          {review.rating && (
                            <div className="flex items-center gap-1.5 bg-yellow-500/10 px-2.5 py-1 rounded-lg border border-yellow-500/20">
                              <Star size={14} className="fill-yellow-500 text-yellow-500" />
                              <span className="text-sm font-bold text-yellow-500">{review.rating}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line italic">"{review.text}"</p>
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-10 opacity-60">
                      <Search className="text-slate-500 w-12 h-12 mb-4" />
                      <p className="text-slate-400 font-medium text-lg">No reviews found</p>
                      <p className="text-slate-500 text-sm">We couldn't locate original reviews for this company.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* AI Gap Analysis Report Container */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 border border-slate-700/50 shadow-2xl relative overflow-hidden">
              {/* Report Decorative Glows */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>

              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-700/50">
                  <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                    <TrendingDown className="text-blue-400 w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">
                      AI Gap Analysis Report
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Machine intelligence powered evaluation</p>
                  </div>
                </div>

                {/* Executive Summary */}
                <div className="mb-10 bg-blue-900/10 border border-blue-500/20 rounded-xl p-6 backdrop-blur-sm shadow-inner">
                  <h3 className="text-sm font-bold tracking-widest text-blue-400 uppercase mb-3 flex items-center gap-2">
                    <Lightbulb size={16} /> Executive Summary
                  </h3>
                  <p className="text-slate-200 leading-loose text-lg">
                    {result.gap_analysis.executive_summary}
                  </p>
                </div>

                {/* Identified Gaps Grid */}
                <div className="mb-10">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <Target className="text-red-400" size={24} />
                    Identified Strategic Gaps
                  </h3>

                  <div className="grid gap-5 md:grid-cols-2">
                    {result.gap_analysis.identified_gaps.map((gap, idx) => {
                      const style = getSeverityStyles(gap.severity);
                      return (
                        <div
                          key={idx}
                          className={`bg-slate-800/60 backdrop-blur-sm p-6 rounded-xl border border-slate-700 hover:border-slate-500 border-l-4 ${style.border} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col`}
                        >
                          <div className="flex items-start justify-between mb-4 gap-4">
                            <h4 className="text-white font-bold text-lg leading-tight flex-1">{gap.title}</h4>
                            <span className={`text-[10px] font-black tracking-widest uppercase px-3 py-1.5 rounded-md border ${style.badge} flex items-center gap-1.5 shrink-0`}>
                              {style.icon}
                              {gap.severity}
                            </span>
                          </div>
                          <p className="text-slate-300 mb-5 leading-relaxed text-sm flex-1">{gap.description}</p>

                          <div className="space-y-3 mt-auto">
                            <div className="bg-slate-900/80 p-4 rounded-lg text-sm text-slate-400 border border-slate-800 hover:border-slate-700 transition-colors">
                              <strong className="text-red-400 uppercase tracking-wider text-xs flex items-center gap-1.5 mb-2"><AlertTriangle size={14} /> Root Cause</strong>
                              <span className="leading-relaxed block">{gap.root_cause}</span>
                            </div>
                            <div className="bg-slate-900/80 p-4 rounded-lg text-sm text-slate-400 border border-slate-800 hover:border-slate-700 transition-colors">
                              <strong className="text-orange-400 uppercase tracking-wider text-xs flex items-center gap-1.5 mb-2"><TrendingDown size={14} /> Business Impact</strong>
                              <span className="leading-relaxed block">{gap.business_impact}</span>
                            </div>
                            <div className="bg-slate-900/80 p-4 rounded-lg text-sm text-slate-400 border border-slate-800 hover:border-slate-700 transition-colors">
                              <strong className="text-slate-300 uppercase tracking-wider text-xs block mb-2">Evidence Detected</strong>
                              <span className="italic leading-relaxed block">{gap.evidence}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Strategic Recommendations */}
                <div className="bg-slate-800/40 rounded-xl p-8 border border-slate-700/50">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                    <CheckCircle2 className="text-green-400" size={24} />
                    Actionable Recommendations
                  </h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {result.gap_analysis.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-4 bg-slate-900/50 p-5 rounded-xl border border-slate-700/50 hover:bg-slate-800/80 hover:border-green-500/30 transition-all duration-300 group">
                        <div className="mt-0.5 min-w-[24px] h-[24px] rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20 group-hover:scale-110 group-hover:bg-green-500 group-hover:text-white transition-all duration-300">
                          <CheckCircle2 size={14} className="text-green-500 group-hover:text-white" />
                        </div>
                        <span className="text-slate-300 leading-relaxed group-hover:text-white transition-colors">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewGapAnalysis;
